/**
 * ## ComposableComponentInstanceItem の型定義
 * - ComposableComponent を 実際に配置する場合のデータ構造
 * - ComposableComponentBuilder 内に配置するコンポーネントの構造
 *   - プロパティ `component` は Vue component であり、String でコンポーネント名を指定
 * - ComposableComponent は プロパティ `children: ComposableComponent[]` を持ち、ネスト構造化が可能
 *   - Children の定義内に、 `slotName: string` を指定することで、Named slot へコンポーネントを配置可能
 * - 基本的には、ComposableComponent のデータ構造に内包される形となる。
 */
export type ComposableComponentInstanceItem = {
  id?: string
  label?: string
  component: string
  children?: ComposableComponentInstanceItem[]
  configuredProps?: Record<string, any>
  styleProps?: {
    classes?: string
    styles?: string
  }
  // Builder 上 でのみの プロパティ, 削除予定かどうか
  isDeleting?: boolean
  // Builder 上 でのみの プロパティ, 親への参照
  parent?: ComposableComponentInstanceItem
  // Builder 上 でのみの プロパティ, closed?
  _isChildClosed?: boolean
  // Builder 上 でのみの プロパティ, is visible?
  _invisible?: boolean
  /**
   * 高度な設定
   */
  advancedSettings?: {
    /**
     * init 時の 関数定義
     * - この関数は、item定義が Vue component として初期化される前に Callされる at `ComposableComponentItemRenderer.vue` の `mounted` ライフサイクル内
     * - async 関数が利用可能, vue コンポーネント (`ComposableComponentItemRenderer`) に thisInstance (this 文脈) にアクセス可能
     * - UIより設定した値にアクセスする場合は、 `thisInstance.refItem.configuredProps` でアクセス可能。
     */
    useBeforeInitFunction?: boolean
    __func__beforeInitFunction?: string
    /**
     * シンボルコンポーネントとして利用する
     */
    useAsSharedComponent?: boolean
    sharedComponentId?: string
  }

  /**
   * TODO: このアイテムが所属する named slot を指定可能, default slot に配置する場合は、指定しない
   */
  // key?: string
  // name?: string
  // description?: string
  // componentType: 'component' | 'container' | 'page'
  // commonSettings: ComposableComponentAttrs
  // slotName?: string
}

export const findStructureWithSharedComponentId = (
  componentInstanceItems: ComposableComponentInstanceItem[],
  sharedComponentId: string,
) => {
  for (const component of componentInstanceItems) {
    if (
      component.advancedSettings?.useAsSharedComponent &&
      component.advancedSettings?.sharedComponentId === sharedComponentId
    ) {
      return component
    }
    if (component.children) {
      const found = findStructureWithSharedComponentId(
        component.children,
        sharedComponentId,
      )
      if (found) {
        return found
      }
    }
  }
  return null
}

/**
 * 共有コンポーネントとして利用するコンポーネントの ID を取得する
 */
export const findSharedComponentIds = (
  componentInstanceItems: ComposableComponentInstanceItem[],
) => {
  const sharedComponentIds = []
  for (const component of componentInstanceItems) {
    if (
      component.advancedSettings?.useAsSharedComponent &&
      component.advancedSettings.sharedComponentId
    ) {
      sharedComponentIds.push(component.advancedSettings.sharedComponentId)
    }
    if (component.children) {
      const found = findSharedComponentIds(component.children)
      if (found) {
        sharedComponentIds.push(...found)
      }
    }
  }
  return sharedComponentIds
}
